/**
 * 1895 Animated Logo — "the birth of cinema"
 * 
 * Full cinematic reveal sequence:
 *   Phase 0: Darkness
 *   Phase 1: Projector glow fades in
 *   Phase 2: The "8" appears (the light source)
 *   Phase 3: "1" and "95" materialise
 *   Phase 4: Film perforations appear
 *   Phase 5: "the birth of cinema" fades in
 *   Phase 6: Settles into breathing loop
 */
import { useState, useEffect } from "react";

export function Logo1895Animated({ width = 280, className = '' }) {
  const [phase, setPhase] = useState(0);
  const h = width * (200 / 560);

  useEffect(() => {
    setPhase(0);
    const timers = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 900),
      setTimeout(() => setPhase(3), 1500),
      setTimeout(() => setPhase(4), 2100),
      setTimeout(() => setPhase(5), 2700),
      setTimeout(() => setPhase(6), 3300),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <svg viewBox="0 0 560 200" xmlns="http://www.w3.org/2000/svg" width={width} height={h} className={className} style={{ borderRadius: 8 }}>
      <defs>
        <radialGradient id="glowA1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#DAA520" stopOpacity="0.35" />
          <stop offset="70%" stopColor="#DAA520" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#DAA520" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="glowA2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Dark background — makes the glow visible */}
      <rect width="560" height="200" fill="#080808" rx="8" />

      {/* Projector glow — fades in first */}
      <circle cx="258" cy="95" r="100" fill="url(#glowA2)" style={{
        opacity: phase >= 1 ? 1 : 0,
        transition: "opacity 1.2s ease-in",
      }}>
        <animate attributeName="r" values="95;105;95" dur="4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0.8;0.5" dur="4s" repeatCount="indefinite" />
      </circle>

      <circle cx="258" cy="95" r="65" fill="url(#glowA1)" style={{
        opacity: phase >= 1 ? 1 : 0,
        transition: "opacity 0.8s ease-in",
      }}>
        <animate attributeName="r" values="60;68;60" dur="4s" repeatCount="indefinite" />
      </circle>

      {/* The "8" appears first — it's the light source */}
      <text x="218" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
        style={{
          fill: "#DAA520",
          opacity: phase >= 2 ? 1 : 0,
          transition: "opacity 0.8s ease-out",
        }}>8</text>

      {/* Then "1" appears */}
      <text x="158" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
        style={{
          fill: "#F5E6C8",
          opacity: phase >= 3 ? 1 : 0,
          transition: "opacity 0.6s ease-out",
        }}>1</text>

      {/* Then "95" appears */}
      <text x="282" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
        style={{
          fill: "#F5E6C8",
          opacity: phase >= 3 ? 1 : 0,
          transition: "opacity 0.6s ease-out",
        }}>95</text>

      {/* Film perforations fade in */}
      {[65, 80, 95, 110, 125].map((y, i) => (
        <g key={i} style={{
          opacity: phase >= 4 ? 1 : 0,
          transition: `opacity 0.5s ease-out ${i * 0.1}s`,
        }}>
          <rect x="110" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5" />
          <rect x="441" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5" />
        </g>
      ))}

      {/* Tagline */}
      <text x="280" y="168" textAnchor="middle" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="12" letterSpacing="5" fontStyle="italic"
        style={{
          fill: "#555",
          opacity: phase >= 5 ? 1 : 0,
          transition: "opacity 1s ease-out",
        }}>the birth of cinema</text>

      {/* Subtle scanning line effect */}
      {phase >= 1 && phase < 4 && (
        <rect x="100" y="0" width="360" height="1" fill="#DAA520" opacity="0.15">
          <animate attributeName="y" from="40" to="160" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.15;0.05;0.15" dur="2s" repeatCount="indefinite" />
        </rect>
      )}
    </svg>
  );
}

export function Logo1895Breathing({ width = 120, className = '' }) {
  const h = width * (200 / 560);
  return (
    <svg viewBox="0 0 560 200" xmlns="http://www.w3.org/2000/svg" width={width} height={h} className={className} style={{ borderRadius: 6 }}>
      <defs>
        <radialGradient id="gB1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#DAA520" stopOpacity="0.35">
            <animate attributeName="stopOpacity" values="0.25;0.45;0.25" dur="3s" repeatCount="indefinite" />
          </stop>
          <stop offset="70%" stopColor="#DAA520" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#DAA520" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="gB2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.1">
            <animate attributeName="stopOpacity" values="0.06;0.15;0.06" dur="3s" repeatCount="indefinite" />
          </stop>
          <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="560" height="200" fill="#080808" rx="6" />
      <circle cx="258" cy="95" r="90" fill="url(#gB2)">
        <animate attributeName="r" values="85;100;85" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="258" cy="95" r="60" fill="url(#gB1)">
        <animate attributeName="r" values="55;65;55" dur="3s" repeatCount="indefinite" />
      </circle>
      <text x="158" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" fill="#F5E6C8" letterSpacing="8">1</text>
      <text x="218" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8" fill="#DAA520">
        8
        <animate attributeName="fill" values="#DAA520;#FFD700;#DAA520" dur="3s" repeatCount="indefinite" />
      </text>
      <text x="282" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" fill="#F5E6C8" letterSpacing="8">95</text>
      {[65, 80, 95, 110, 125].map((y, i) => (
        <g key={i}>
          <rect x="110" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5">
            <animate attributeName="opacity" values="0.5;0.8;0.5" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </rect>
          <rect x="441" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5">
            <animate attributeName="opacity" values="0.5;0.8;0.5" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </rect>
        </g>
      ))}
      <text x="280" y="168" textAnchor="middle" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="12" fill="#555" letterSpacing="5" fontStyle="italic">the birth of cinema</text>
    </svg>
  );
}

export function Logo1895Icon({ width = 32, className = '' }) {
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" width={width} height={width} className={className} style={{ borderRadius: 4 }}>
      <defs>
        <radialGradient id="gI" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#DAA520" stopOpacity="0.4">
            <animate attributeName="stopOpacity" values="0.3;0.5;0.3" dur="3s" repeatCount="indefinite" />
          </stop>
          <stop offset="100%" stopColor="#DAA520" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="#080808" rx="4" />
      <circle cx="50" cy="46" r="32" fill="url(#gI)">
        <animate attributeName="r" values="30;34;30" dur="3s" repeatCount="indefinite" />
      </circle>
      <text x="12" y="62" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="48" fill="#F5E6C8" letterSpacing="2">1</text>
      <text x="36" y="62" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="48" letterSpacing="2" fill="#DAA520">
        8
        <animate attributeName="fill" values="#DAA520;#FFD700;#DAA520" dur="3s" repeatCount="indefinite" />
      </text>
      <text x="60" y="62" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="48" fill="#F5E6C8" letterSpacing="2">95</text>
      {[30, 40, 50, 60].map((y, i) => (
        <g key={i}>
          <rect x="2" y={y - 2} width="4" height="3" rx="0.5" fill="none" stroke="#444" strokeWidth="0.4">
            <animate attributeName="opacity" values="0.4;0.7;0.4" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </rect>
          <rect x="94" y={y - 2} width="4" height="3" rx="0.5" fill="none" stroke="#444" strokeWidth="0.4">
            <animate attributeName="opacity" values="0.4;0.7;0.4" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </rect>
        </g>
      ))}
      <text x="50" y="82" textAnchor="middle" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="6.5" fill="#555" letterSpacing="2" fontStyle="italic">the birth of cinema</text>
    </svg>
  );
}

// Default export for convenience
export function Logo1895({ variant = 'breathing', width = 120, className = '' }) {
  if (variant === 'animated') return <Logo1895Animated width={width} className={className} />;
  if (variant === 'icon') return <Logo1895Icon width={width} className={className} />;
  if (variant === 'full') return <Logo1895Breathing width={width} className={className} />;
  return <Logo1895Breathing width={width} className={className} />;
}

export default Logo1895;
